import { Dropdown } from 'antd';
import { Bell } from 'lucide-react';
import { Link } from 'react-router';
import { ARRANGEMENT_TYPES, readableDate, type AwaitedCommandDto, type NotificationEntryDto } from '@pams/shared';
import { istDateTime } from '@/lib/analyticsReport';
import { StatusDot } from '@/components/StatusDot';
import { Button } from '@/components/ui/button';
import { useNotifications } from '@/hooks/useNotifications';
import { bellForceClause } from '@/lib/statusPresentation';
import { awaitedRolesPhrase } from '@/lib/commandWords';
import { NOTIFICATION_COPY } from '@/components/notificationCopy';
import { cn } from '@/lib/utils';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [C1g §1d] THE NOTIFICATION BELL — outstanding work, in the utility bar, on every page.
 *
 *  ⚠️ `UtilityBar`'s OWN COMMENT SAID THIS COULD NOT EXIST YET: "a notification bell that
 *  notifies nothing is the same defect as a ⌘K button that opens nothing." It notifies
 *  something now — §1a's derived outstanding work — and that comment is rewritten rather than
 *  left contradicting the component beside it.
 *
 *  ⚠️ AN ANTD `Dropdown`, LIKE `AvatarMenu` — the documented exception to §8's "shadcn/ui for
 *  the shell", and for its reason verbatim: `@radix-ui/react-dropdown-menu` pulls in
 *  `@floating-ui/dom`, which constructs `ResizeObserver` directly and would need a jsdom stub
 *  in `vitest.setup.ts`. Zero new dependencies and zero new test setup.
 *
 *  ⚠️ `trigger={['click']}` IS NOT OPTIONAL. CLAUDE.md §8: an AntD `Dropdown` with no trigger
 *  opens on HOVER — click 0, Enter 0, Space 0 — which shipped once on the instruction line's
 *  `⋯` and was unreachable by keyboard entirely. Every `Dropdown` in this repository states
 *  its trigger.
 *
 *  ⚠️ IT IS IN `e2e/fixtures/overlays.ts`, BECAUSE `census.spec.ts` FAILS ON AN OVERLAY THAT
 *  IS NEITHER SWEPT NOR EXEMPTED. That guard keys on the component NAME, so adding a
 *  `Dropdown` here without the entry is a red census run rather than a silent hole.
 *
 *  ─── ⚠️ THE COUNT AND THE DOT ARE TWO DIFFERENT FACTS (§1b) ─────────────────────
 *
 *  The NUMBER is outstanding work — unit-wide, derived, cleared only by naming somebody. The
 *  DOT is per officer and clears when they open this. Rendering one and not the other is how
 *  a bell either lies about somebody else's work or shows forty week-old items for ever.
 *
 *  ─── ⚠️ [UX1 §4d] IT IS PROMINENT WHEN THERE IS WORK — BY WEIGHT AND CONTRAST ONLY ───────
 *
 *  ⚠️ NO NEW COLOUR SEMANTICS, AND NONE ARE AVAILABLE. Red, amber and green MEAN fulfilment
 *  state on every surface in this product ([C1h] §2g), so a bell that reached for one of them
 *  to say "look at me" would be saying something else entirely. What is left is weight,
 *  contrast and size, and all three are token-driven:
 *
 *    · the icon steps from `muted-foreground` to `foreground` and from stroke 2 to 2.25;
 *    · the badge steps from `font-medium` to `font-semibold` and gains a `ring-background`,
 *      which is what separates a small red disc from the glyph behind it.
 *
 *  ⚠️ THE RESTING STATE IS WHAT MOVED. The bell has always inherited `foreground`, so
 *  prominence could only come from making the QUIET state quieter — `PageHeader`'s own
 *  argument ("the icon is muted while the title is full contrast — that is most of what makes
 *  the chrome recede"), applied to the one chrome control that sometimes has something to say.
 *
 *  ⚠️ AND `data-outstanding` IS ON THE TRIGGER BECAUSE jsdom RESOLVES NO CASCADE. A class is
 *  readable and a rendered weight is not, so the assertion reads the attribute and the classes;
 *  `notifications.spec.ts` measures the rectangle in a browser.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
/**
 * ⚠️ [C6a] `commandAwaited` IS READ AS POSSIBLY ABSENT. A web build running ahead of its API gets
 * entries with no such key, and `awaitedRolesPhrase` iterating `undefined` would throw during the
 * render of a control mounted on EVERY page — the outermost error boundary would then replace the
 * whole app. [C5c §8]'s `draftExpiry` rule for a newer client: the older payload renders as though
 * nothing is awaited, which is exactly what that API knew. Normalised once, so the force clause and
 * the command phrase read one value.
 */
const withAwaited = (
  entry: NotificationEntryDto,
): Omit<NotificationEntryDto, 'commandAwaited'> & { commandAwaited: readonly AwaitedCommandDto[] } => {
  const awaited: readonly AwaitedCommandDto[] | undefined = entry.commandAwaited;
  return { ...entry, commandAwaited: awaited ?? [] };
};

/**
 * ⚠️ [C6a review] THE FORCE CLAUSE IS HANDED A YES/NO, NEVER THE ROLES. `bellForceClause` lives in the
 * file that words every status surface, which stays off the command vocabulary
 * (`commandIsolation.test.ts` (i)); this is the one place the bell's command field decides whether the
 * force speaks, and both the entries and the "N more" figure read it through here.
 */
const forceClauseOf = (entry: ReturnType<typeof withAwaited>): ReturnType<typeof bellForceClause> =>
  bellForceClause(entry, entry.commandAwaited[0] !== undefined);

export const NotificationBell = () => {
  const { data, notices, enabled, markSeen } = useNotifications();
  if (!enabled) return null;

  const total = data?.total ?? 0;
  const newCount = data?.newCount ?? 0;
  const entries = (data?.entries ?? []).map(withAwaited);
  /*
   * ⚠️ [P7B §5d] TYPED ORDERS ARRIVE FOLDED, ONE ROW PER TYPE — `typeGroups` is OPTIONAL and absent
   * when no typed order has work left, so a payload without it renders exactly as before. `total`,
   * `newCount` and `forceTotal` stay INCLUSIVE (the badge is still the exact number of orders with
   * work left); only the "N more" figure subtracts the folds' own force share, because the folds are
   * shown above it and Assign PA would otherwise be promised them twice.
   */
  const typeGroups = data?.typeGroups ?? [];
  const foldedForce = typeGroups.reduce((sum, group) => sum + group.forceTotal, 0);
  const noticeItems = notices?.items ?? [];
  const unseenNotices = notices?.unseenCount ?? 0;
  /*
   * [C6a] Assign PA's unlisted remainder — `forceTotal` less the entries shown with a force clause,
   * which is the server's `forceOpen` in every shape but the one `bellForceClause` names.
   * ⚠️ `?? total` for an API that predates the key: every entry it sent was force work then.
   * ⚠️ [P7B §5d] …and less the folded types' force work, which the type rows already stand for.
   */
  const forceTotal: number | undefined = data?.forceTotal;
  const moreInInbox = Math.max(
    0,
    (forceTotal ?? total) - foldedForce - entries.filter((entry) => forceClauseOf(entry) !== null).length,
  );

  const items = [
    /*
     * ⚠️ [P7B §6b] THE NOTICES SECTION — WHAT WAS TOLD, ABOVE WHAT IS LEFT. Rendered only when the
     * caller has any, so a bell with none is the bell it was. A notice outlives its order (a reverted
     * draft may be deleted), so it links only while the server says the caller can still open it.
     */
    ...(noticeItems.length === 0
      ? []
      : [
          {
            key: 'notices-heading',
            type: 'group' as const,
            label: <span className="block py-1 text-xs text-muted-foreground">{NOTIFICATION_COPY.noticesHeading}</span>,
          },
          ...noticeItems.map((notice) => {
            const body = (
              <span className="flex max-w-80 flex-col gap-0.5 py-1" data-testid="notification-notice">
                <span className="flex items-center gap-2">
                  {notice.seenAt === null ? (
                    <span aria-hidden data-testid="notice-new" className="size-1.5 shrink-0 rounded-full bg-primary" />
                  ) : null}
                  <span className="min-w-0 text-sm text-foreground">
                    {NOTIFICATION_COPY.notice(notice.paNumber ?? notice.reference, notice.title)}
                  </span>
                </span>
                <span className="text-xs text-muted-foreground">{NOTIFICATION_COPY.noticeReason(notice.reason)}</span>
                <span className="text-xs text-muted-foreground">{istDateTime(notice.createdAt)}</span>
              </span>
            );
            return {
              key: `notice-${notice.id}`,
              label: notice.arrangementVisible ? (
                <Link to={`/arrangements/${notice.arrangementId}`}>{body}</Link>
              ) : (
                body
              ),
            };
          }),
        ]),
    {
      key: 'heading',
      type: 'group' as const,
      label: (
        <span className="block py-1 text-xs text-muted-foreground">
          {data ? NOTIFICATION_COPY.window(data.from, data.to) : NOTIFICATION_COPY.loading}
        </span>
      ),
    },
    /*
     * ⚠️ [P7B §5d] ONE ROW PER TYPE, LINKING TO ASSIGN PA — where its orders are filled. Before the
     * untyped entries, which keep their own eight and their own words unchanged.
     */
    ...typeGroups.map((group) => ({
      key: `type-${group.type}`,
      label: (
        <Link to="/inbox" className="block max-w-80 py-1 text-sm" data-testid="notification-type-group">
          {NOTIFICATION_COPY.typeGroup(ARRANGEMENT_TYPES[group.type].label, group.total, group.newCount)}
        </Link>
      ),
    })),
    /* ⚠️ [P7B §5d] "Nothing outstanding" only when there is neither an entry NOR a folded type. */
    ...(entries.length === 0 && typeGroups.length === 0
      ? [
          {
            key: 'empty',
            /* ⚠️ A real answer, never a blank panel — `TileEmpty`'s rule in the chrome. */
            label: <span className="block max-w-72 py-2 text-sm text-muted-foreground">{NOTIFICATION_COPY.empty}</span>,
            disabled: true,
          },
        ]
      : entries.map((entry) => {
          const force = forceClauseOf(entry);
          const command = awaitedRolesPhrase(entry.commandAwaited);
          return {
            key: entry.arrangementId,
            label:
              (
                /*
                 * ⚠️ A REAL `<a>`, so ⌘-click and copy-link work — `NameCell`'s rule. It goes to
                 * the INBOX rather than to the order, because the entry IS a naming obligation and
                 * `/inbox` is where a nodal does the naming.
                 *
                 * ⚠️ [C6a] …EXCEPT WHERE THE ONLY OPEN WORK IS A COMMAND ROLE. Assign PA lists force
                 * shares alone, so an entry with no force clause would send the division to a list
                 * with nothing of theirs on it; it goes to the soonest duty still awaiting the role,
                 * which is where its fill screen names the officer. `commandDutyId` is `null` when no
                 * awaited duty can still take a name — the inbox is then the only honest door left.
                 *
                 * ⚠️ [C6a review] NO `?division=`, WHICH THE PLAN ASKED FOR — A RECORDED DEVIATION. An entry
                 * is one per ORDER and may carry roles awaited from several divisions (the CP office's and
                 * a super admin's bell), so one division id would be an arbitrary pick; and the unnarrowed
                 * sheet already shows each reader exactly the families their seat scope admits — a
                 * division's or a unit's own, every one for HQ. Narrowing would HIDE an awaited family
                 * the entry itself names.
                 */
                <Link
                  to={
                    force === null && command !== null && entry.commandDutyId !== null
                      ? `/duties/${entry.commandDutyId}`
                      : '/inbox'
                  }
                  className="flex max-w-80 flex-col gap-0.5 py-1"
                  data-testid="notification-entry"
                >
                  <span className="flex items-center gap-2">
                    {entry.isNew ? (
                      <span
                        aria-hidden
                        data-testid="notification-new"
                        className="size-1.5 shrink-0 rounded-full bg-primary"
                      />
                    ) : null}
                    <span className="min-w-0 truncate text-sm text-foreground" title={entry.displayTitle}>
                      {entry.displayTitle}
                    </span>
                  </span>
                  {/* ⚠️ [C6a] `flex-wrap`: the command phrase can outrun `max-w-80`, and a line that
                    does not wrap overflows the popover rather than growing it. */}
                  <span className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                    <span>{readableDate(entry.firstDutyDate) ?? entry.firstDutyDate}</span>
                    {/*
                     * ⚠️ [C6a] THE FORCE CLAUSE ONLY WHILE THE FORCE IS OPEN — `bellForceClause`. It
                     * is `namedOf` and `fillBadge` as before ([C5b §3], `docs/status-matrix.md`'s
                     * `web.bell` row), over the entry's REAL `closedShort` now, and absent when the
                     * force is finished, or was never asked of an order awaiting a role: the words
                     * are then the phrase alone, never `0 of 12 named · All Filled`.
                     */}
                    {force === null ? null : (
                      <>
                        <span aria-hidden>·</span>
                        <span>{force.count}</span>
                        <StatusDot tone={force.badge.tone} label={force.badge.label} />
                      </>
                    )}
                    {/*
                     * ⚠️ [C6a] THE ROLE, BY NAME, AND WHO OWES IT — `In-charge awaited from CD`. The
                     * user's decision: the force wording is unchanged and a command role adds WORDS,
                     * never a number, beside it (FR-5.2.6b).
                     */}
                    {command === null ? null : (
                      <>
                        <span aria-hidden>·</span>
                        <span data-testid="notification-command">{command}</span>
                      </>
                    )}
                  </span>
                </Link>
              ),
          };
        })),
    /*
     * ⚠️ [C6a] "N MORE IN ASSIGN PA" PROMISES ONLY WHAT ASSIGN PA LISTS. `total` counts an order
     * whose only open work is a command role, and the inbox lists force shares alone — so the
     * figure is the server's `forceTotal` less the force entries already shown here, and a popover
     * whose remaining orders are all command-only offers no inbox link that would open onto none.
     */
    ...(moreInInbox > 0
      ? [
          {
            key: 'more',
            label: (
              <Link to="/inbox" className="block py-1 text-sm">
                {NOTIFICATION_COPY.more(moreInInbox)}
              </Link>
            ),
          },
        ]
      : []),
  ];

  return (
    <Dropdown
      menu={{ items }}
      trigger={['click']}
      placement="bottomRight"
      overlayClassName="min-w-72"
      /*
       * ⚠️ MARKED SEEN ON OPEN, AND ONLY ON OPEN. `markSeen` is a no-op when nothing is new,
       * so a nodal opening a bell they have already read writes nothing at all.
       */
      onOpenChange={(open) => {
        if (open) markSeen();
      }}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn('relative [&_svg]:size-5', total > 0 ? 'text-foreground' : 'text-muted-foreground')}
        data-testid="notification-bell"
        data-outstanding={total > 0 ? 'true' : 'false'}
        aria-label={NOTIFICATION_COPY.trigger(total, newCount, unseenNotices)}
      >
        {/* ⚠️ A `stroke-width` ATTRIBUTE, NOT A CSS LITERAL — lucide takes it as a prop, so the
            weight step is assertable and is not a colour. */}
        <Bell aria-hidden strokeWidth={total > 0 ? 2.25 : 2} />
        {total > 0 ? (
          /*
           * ⚠️ `aria-hidden`, BECAUSE THE NUMBER IS ALREADY IN THE ACCESSIBLE NAME. Two
           * readings of one fact is what `CoverageBar`'s R2 forbids — the bar is decoration
           * and the sentence is the content.
           */
          <span
            aria-hidden
            data-testid="notification-count"
            /* ⚠️ [UX1 §4d] `ring-background` IS THE BIGGEST LEGIBILITY GAIN HERE and costs the
               rules nothing: `--color-background` is mapped in `styles/index.css`, so it
               resolves through the same custom property a nested `ThemeScope` overrides. The
               offset moves from 0.5 to 1 to absorb the ring's 2px. */
            className="absolute -right-1 -top-1 min-w-[1.125rem] rounded-full bg-destructive px-1 text-[11px] font-semibold leading-[1.125rem] text-destructive-foreground ring-2 ring-background"
          >
            {total}
          </span>
        ) : null}
        {/*
          ⚠️ [P7B §6b] UNSEEN NOTICES ARE A DOT, NEVER THE NUMBER — the badge stays the derived count of
          orders with work left. `aria-hidden`, because the words are in the accessible name.
        */}
        {unseenNotices > 0 ? (
          <span
            aria-hidden
            data-testid="notification-notice-dot"
            className="absolute -bottom-0.5 -right-0.5 size-2 rounded-full bg-primary ring-2 ring-background"
          />
        ) : null}
      </Button>
    </Dropdown>
  );
};
